'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  forwardRef,
  useCallback,
  useRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';

import { cn } from '../utils/cn';
import { useWheelScrollFix } from '../utils/use-wheel-scroll-fix';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => {
  // Keep our own handle on the content node (for the wheel-scroll fix below)
  // while still honouring whatever ref the caller passed.
  const innerRef = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  // Restore wheel / trackpad scrolling for scrollable areas inside this popover
  // (combobox lists, date-picker year lists, ...) when it's opened from inside a
  // vaul Drawer / scroll-locked dialog, which otherwise cancel native wheel
  // scrolling on this portaled subtree. See useWheelScrollFix.
  useWheelScrollFix(innerRef);

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={setRef}
        align={align}
        sideOffset={sideOffset}
        // Also hand vaul its drag escape-hatch so touch (touchscreen / pen)
        // pointer gestures bubbling back through the portal don't get turned
        // into a drawer drag. Inert when there's no vaul drawer.
        data-vaul-no-drag="true"
        className={cn(
          // z-[110] so popovers always float above the app's dialogs (z-100
          // in InsurancePickerDialog / VisaPickerDialog / DayItemPickerModal).
          // Radix already portals to <body>, so we're only fighting z-order.
          'bg-popover text-popover-foreground z-[110] w-72 rounded-md border p-4 shadow-md outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
